import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useBrand } from '../../context/BrandContext';
import { useUserAvatar } from '../../hooks/useUserAvatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  LayoutDashboard,
  ScanSearch,
  Calculator,
  Bot,
  List,
  AlertTriangle,
  Settings,
  PanelLeftClose,
  PanelLeft,
  LogOut,
  UserCircle,
  TrendingUp,
  ChevronDown,
  Search,
  Languages,
  MessageSquare,
  FlaskConical,
  Flag,
  Image as ImageIcon,
  Megaphone,
  Mail,
  Gamepad2,
  Repeat2,
  BarChart3,
  LineChart,
  Sparkles,
  Gift,
  Snowflake,
  HandCoins,
  PhoneOutgoing,
  CalendarDays,
  Rocket,
  SlidersHorizontal,
} from 'lucide-react';
import cashyLogoSrc from '../../assets/cashy-logo.png';

const BRAND_COLORS = {
  bitstarz: '#e63950',
  cashy: '#f5c518',
};

const NAV_CONFIG = [
  {
    type: 'group',
    key: 'vip',
    label: 'VIP Dashboard',
    Icon: LayoutDashboard,
    module: 'vip',
    permission: 'view:vip_dashboard',
    items: [
      { label: 'Dashboard',    path: '/vip-dashboard',     Icon: LayoutDashboard },
      { label: 'Bonus Impact', path: '/player-trajectory', Icon: TrendingUp },
      { label: 'Player lookup',path: '/player-search',     Icon: Search },
      // Only brands whose warehouse has loyalty.anniversary_bonus_moderation; others list
      // vip.anniversary_bonuses as unsupported.
      { label: 'Anniversary Bonuses', path: '/anniversary-bonuses', feature: 'vip.anniversary_bonuses', Icon: Gift },
      // Same for loyalty.christmas_bonus_moderation / vip.christmas_bonuses.
      { label: 'Christmas Bonuses', path: '/christmas-bonuses', feature: 'vip.christmas_bonuses', Icon: Snowflake },
      // VIP comp requests read db-data (vip data_db store); brands without it list vip.bonus_requests
      // as unsupported.
      { label: 'Bonus Requests', path: '/bonus-requests', feature: 'vip.bonus_requests', Icon: HandCoins },
      // The contact log lives in db-data too; brands without it list vip.contact_tracker as unsupported.
      { label: 'Contact Tracker', path: '/contact-tracker', feature: 'vip.contact_tracker', Icon: PhoneOutgoing },
    ],
  },
  // The private VIP dashboard: one agent's book from the temp_ tables. Its own group, because the
  // VIP group above requires view:vip_dashboard, which its holder does not have.
  {
    type: 'group',
    key: 'vip_private',
    label: 'My VIPs',
    Icon: LayoutDashboard,
    module: 'vip_private',
    permission: 'view:vip_private',
    items: [
      { label: 'Dashboard',     path: '/vip-private',               Icon: LayoutDashboard },
      { label: 'Player lookup', path: '/vip-private/player-search', Icon: Search },
      { label: 'Bonuses',       path: '/vip-private/bonuses',       Icon: HandCoins },
    ],
  },
  {
    type: 'group',
    key: 'game',
    label: 'Game Dashboard',
    Icon: Gamepad2,
    // No group module: Game Analysis is a module of its own (bi-game-analysis captures, Bitstarz only),
    // so each item is gated by its own module; the whole group shares the audience, hence the permission.
    module: null,
    permission: 'view:game_department',
    items: [
      { label: 'Dashboard',             path: '/game-department',                       module: 'game_department', Icon: Gamepad2 },
      { label: 'Game Metrics',          path: '/game-department/metrics',               module: 'game_department', Icon: BarChart3 },
      { label: 'Game Performance',      path: '/game-department/performance',           module: 'game_department', Icon: LineChart },
      { label: 'Game Releases',         path: '/game-department/releases',              module: 'game_department', Icon: CalendarDays },
      { label: 'Pre-release Benchmark', path: '/game-department/pre-release-benchmark', module: 'game_department', Icon: Rocket },
      { label: 'Game Stickiness',       path: '/game-department/stickiness',            module: 'game_department', Icon: Repeat2 },
      { label: 'Ask AI',                path: '/game-department/ask-ai',                module: 'game_department', Icon: Sparkles },
      { label: 'Game Analysis',         path: '/game-analysis',                         module: 'game_analysis',   Icon: ScanSearch },
    ],
  },
  { label: 'Conversion',     path: '/conversion',              permission: 'view:conversion',        module: 'conversion',    Icon: Calculator },
  { label: 'Bot Evaluation', path: '/bot-evaluation',          permission: 'view:bot_evaluation',    module: 'chatbot',       Icon: Bot },
  { label: 'Feedback List',  path: '/bot-evaluation/feedback', permission: 'view:bot_feedback_list', module: 'chatbot',       Icon: List, subItem: true },
  { label: 'Casino Issues',  path: '/casino-issues',           permission: 'manage:casino_issues',   module: 'casino_issues', Icon: AlertTriangle },
  { label: 'CRM Campaigns',  path: '/crm-campaigns',           permission: 'manage:crm_campaigns',   module: 'crm_campaigns', Icon: Megaphone },
  {
    type: 'group',
    key: 'chat_insights',
    label: 'Chat Insights',
    Icon: MessageSquare,
    module: null,
    permission: null,
    items: [
      // Both livechat items share one module, but they need different warehouse tables: CRM
      // Customer Intelligence additionally requires the analyzed-chats / di_patterns tables
      // (livechat_mining.* or pulse_mining.*) and the crm.di_*_scd tables, which a brand can be
      // missing while still supporting Flagged Chats. Hence the feature key — a brand lists
      // livechat_mining.crm_intelligence as unsupported to enable Flagged Chats alone.
      { label: 'CRM Customer Intelligence', path: '/livechat-mining', permission: 'view:livechat_mining', module: 'livechat_mining', feature: 'livechat_mining.crm_intelligence', Icon: MessageSquare },
      { label: 'Flagged Chats',             path: '/flagged-chats',   permission: 'view:flagged_chats',   module: 'livechat_mining', Icon: Flag },
    ],
  },
  {
    type: 'group',
    key: 'translation',
    label: 'Translation Playground',
    Icon: Languages,
    items: [
      { label: 'Chatbot',      path: '/translation-playground', permission: 'view:translation_chatbot',      module: 'translation_chat', Icon: MessageSquare },
      { label: 'Localization', path: '/translation-lab',         permission: 'view:translation_localization', module: 'translation_lab',  Icon: FlaskConical },
      // Reviewing the designers' banner copy is translation work like the two above it, so it
      // belongs in this group rather than beside it. Minting the plugin token is not — that is a
      // personal credential, and it lives on the profile page.
      { label: 'Banner translations', path: '/figma-review',     permission: 'view:figma_localization',       module: 'figma_loc',        Icon: ImageIcon },
      { label: 'CRM emails',     path: '/crm-email',             permission: 'view:crm_email',                 module: 'crm_email',         Icon: Mail },
    ],
  },
  // Its route needs view:vip_dashboard; without the same gate here a user holding only
  // view:vip_private saw an entry that bounced them away.
  { label: 'Settings',       path: '/settings',                permission: 'view:vip_dashboard',     module: 'vip', feature: 'vip.llm_settings', Icon: Settings },
  // Key-value configuration, scoped to the active brand like everything else. No module gating —
  // it needs no per-brand wiring. Tokens for scripts are minted in Settings, like the Figma and
  // Customer.io ones.
  { label: 'Configuration',  path: '/configuration',           permission: 'manage:configuration',  module: null,                            Icon: SlidersHorizontal },
];

const BRAND_ICONS = {
  bitstarz: (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="var(--brand)" className="shrink-0">
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
    </svg>
  ),
  cashy: (
    <img src={cashyLogoSrc} alt="Cashy" className="h-4 w-4 object-contain shrink-0" />
  ),
};

function BrandLogoChip({ brandKey }) {
  return (
    <div className="sidebar-logo-chip">
      {BRAND_ICONS[brandKey] || BRAND_ICONS.bitstarz}
    </div>
  );
}

function BrandSwitcher({ open }) {
  const { brands, activeBrand, setActiveBrand } = useBrand();

  if (!brands || brands.length === 0) {
    return (
      <div className="flex items-center gap-2">
        <BrandLogoChip brandKey="bitstarz" />
        {open && (
          <span className="font-display text-base tracking-tight text-ink">
            Bit<span style={{ color: 'var(--brand)' }}>Starz</span>
          </span>
        )}
      </div>
    );
  }

  if (!open) {
    return null;
  }

  return (
    <Select value={activeBrand || ''} onValueChange={setActiveBrand}>
      <SelectTrigger className="h-8 border-0 bg-transparent text-white hover:bg-surface-2 focus:ring-0 focus:ring-offset-0 px-1 gap-2 font-display text-base tracking-tight">
        <BrandLogoChip brandKey={activeBrand} />
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="bg-surface-1 border-white/10">
        {brands.map(b => (
          <SelectItem
            key={b.key}
            value={b.key}
            className="text-ink-dim focus:bg-surface-2 focus:text-ink"
          >
            {b.display_name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function useGroupExpanded(key, defaultOpen = true) {
  const storageKey = `sidebar_group_${key}`;
  const [expanded, setExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored !== null ? stored === 'true' : defaultOpen;
    } catch { return defaultOpen; }
  });

  const toggle = () => setExpanded(prev => {
    const next = !prev;
    try { localStorage.setItem(storageKey, String(next)); } catch {}
    return next;
  });

  return [expanded, toggle];
}

function NavGroup({ group, sidebarOpen, navigate, location, isItemAllowed }) {
  const [expanded, toggle] = useGroupExpanded(group.key);

  // Items are gated the same way flat entries are — module against the active brand, permission
  // against the user — because a group without its own module (Translation Playground, Chat
  // Insights) is exactly as visible as the brand's modules allow, item by item.
  const visibleItems = group.items.filter(isItemAllowed);

  if (visibleItems.length === 0) return null;

  const isAnyChildActive = visibleItems.some(item => location.pathname === item.path);

  if (!sidebarOpen) {
    const btn = (
      <button
        onClick={() => navigate(visibleItems[0].path)}
        className={`flex items-center justify-center w-full p-2 rounded-lg transition-colors sidebar-nav-hover ${
          isAnyChildActive
            ? 'sidebar-nav-active'
            : 'text-ink-dim hover:text-ink'
        }`}
      >
        <group.Icon className="h-4 w-4 shrink-0" />
      </button>
    );
    return (
      <li>
        <Tooltip>
          <TooltipTrigger asChild>{btn}</TooltipTrigger>
          <TooltipContent side="right">{group.label}</TooltipContent>
        </Tooltip>
      </li>
    );
  }

  return (
    <li>
      <button
        onClick={toggle}
        style={isAnyChildActive && !expanded ? { color: 'var(--brand)' } : {}}
        className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg transition-colors text-sm font-medium sidebar-nav-hover ${
          isAnyChildActive && !expanded
            ? 'hover:text-ink'
            : 'text-ink-dim hover:text-ink'
        }`}
      >
        <group.Icon className="h-4 w-4 shrink-0" />
        <span className="truncate flex-1 text-left">{group.label}</span>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform duration-150 ${expanded ? '' : '-rotate-90'}`} />
      </button>
      {expanded && (
        <ul className="mt-0.5 space-y-0.5">
          {visibleItems.map(item => {
            const isActive = location.pathname === item.path;
            return (
              <li key={item.path}>
                <button
                  onClick={() => navigate(item.path)}
                  className={`flex items-center gap-2.5 w-full pl-9 pr-3 py-1.5 rounded-lg transition-colors text-xs font-medium sidebar-nav-hover ${
                    isActive
                      ? 'sidebar-nav-active'
                      : 'text-ink-dim hover:text-ink'
                  }`}
                >
                  <item.Icon className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{item.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

function UserSection({ open }) {
  const { user, logout } = useAuth();
  const { avatar, configured } = useUserAvatar();
  const navigate = useNavigate();

  const avatarEl = avatar
    ? <img src={`/avatars/${avatar}.png`} alt={user?.name} className="w-full h-full object-cover" />
    : <UserCircle className="w-4 h-4 text-ink-dim" />;

  const badge = !configured && (
    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-amber-500 ring-2 ring-surface-1 animate-pulse" />
  );

  if (!open) {
    return (
      <div className="px-2 pb-3 pt-2 border-t border-white/10">
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => navigate('/profile')}
              className="flex items-center justify-center w-full p-1 rounded-lg hover:bg-surface-2 transition-colors"
            >
              <div className="relative shrink-0">
                <div className="w-7 h-7 rounded-full overflow-hidden bg-surface-2 flex items-center justify-center ring-1 ring-white/10">
                  {avatarEl}
                </div>
                {badge}
              </div>
            </button>
          </TooltipTrigger>
          <TooltipContent side="right">
            {!configured ? 'Set your avatar' : `${user?.name} · Settings`}
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="px-3 pb-3 pt-2 border-t border-white/10 flex items-center gap-2">
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => navigate('/profile')}
            className="relative shrink-0"
          >
            <div className="w-8 h-8 rounded-full overflow-hidden bg-surface-2 flex items-center justify-center ring-1 ring-white/10 hover:ring-primary-500/50 transition-all">
              {avatarEl}
            </div>
            {badge}
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">
          {!configured ? 'Set your avatar' : 'Settings'}
        </TooltipContent>
      </Tooltip>
      <span className="text-xs font-medium text-ink-dim truncate flex-1">{user?.name}</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={logout}
            className="p-1.5 rounded-md text-ink-dim hover:text-primary-400 hover:bg-surface-2 transition-colors shrink-0"
          >
            <LogOut className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="right">Logout</TooltipContent>
      </Tooltip>
    </div>
  );
}

export default function Sidebar({ open, onToggle }) {
  const { hasPermission } = useAuth();
  const { activeBrand, activeBrandInfo } = useBrand();
  const navigate = useNavigate();
  const location = useLocation();

  const brandModules = activeBrandInfo?.modules;
  const brandUnsupported = activeBrandInfo?.unsupported || [];
  const brandColor = BRAND_COLORS[activeBrand] || BRAND_COLORS.bitstarz;

  // Gates one nav entry (flat or inside a group): the active brand must have the entry's module,
  // the brand must not list the feature as unsupported, and the user needs the permission.
  // !item.permission rather than === null: group sub-items (VIP Dashboard) carry no permission
  // field at all, and hasPermission(undefined) is false.
  const isItemAllowed = item => {
    if (item.module && brandModules && !brandModules.includes(item.module)) return false;
    if (item.feature && brandUnsupported.includes(item.feature)) return false;
    return !item.permission || hasPermission(item.permission);
  };

  const isGroupAllowed = group => {
    if (group.module && brandModules && !brandModules.includes(group.module)) return false;
    return !group.permission || hasPermission(group.permission);
  };

  const visibleConfig = NAV_CONFIG.filter(entry =>
    entry.type === 'group' ? isGroupAllowed(entry) : isItemAllowed(entry)
  );

  const hasAnyAccess = visibleConfig.length > 0;

  return (
    <aside
      data-brand={activeBrand || 'bitstarz'}
      className="fixed inset-y-0 left-0 z-40 flex flex-col border-r border-white/10 bg-surface-1 transition-all duration-200"
      style={{ width: open ? 240 : 56, '--brand': brandColor }}
    >
      <div className="sidebar-rail" />

      <div className={`flex items-center ${open ? 'justify-between px-4' : 'justify-center px-0'} border-b border-white/10 h-14`}>
        <BrandSwitcher open={open} />
        {open && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onToggle}
                className="p-1.5 rounded-md text-ink-dim hover:text-ink hover:bg-surface-2 transition-colors"
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Collapse sidebar</TooltipContent>
          </Tooltip>
        )}
        {!open && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={onToggle}
                className="p-1.5 rounded-md text-ink-dim hover:text-ink hover:bg-surface-2 transition-colors"
              >
                <PanelLeft className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Expand sidebar</TooltipContent>
          </Tooltip>
        )}
      </div>

      <nav className="flex-1 pt-2 px-2 overflow-y-auto">
        {!hasAnyAccess && open ? (
          <p className="px-3 py-3 text-sm font-medium text-ink-dim">
            Contact your administrator to get access.
          </p>
        ) : (
          <ul className="space-y-0.5">
            {visibleConfig.map(entry => {
              if (entry.type === 'group') {
                return (
                  <NavGroup
                    key={entry.key}
                    group={entry}
                    sidebarOpen={open}
                    navigate={navigate}
                    location={location}
                    isItemAllowed={isItemAllowed}
                  />
                );
              }

              const isActive = location.pathname === entry.path;
              const btn = (
                <button
                  key={entry.path}
                  onClick={() => navigate(entry.path)}
                  className={`
                    flex items-center gap-3 w-full rounded-lg transition-colors text-sm font-medium sidebar-nav-hover
                    ${open ? (entry.subItem ? 'pl-5 pr-3 py-2' : 'px-3 py-2') : 'p-2 justify-center'}
                    ${isActive ? 'sidebar-nav-active' : 'text-ink-dim hover:text-ink'}
                    ${entry.subItem ? 'text-xs' : ''}
                  `}
                >
                  <entry.Icon className="h-4 w-4 shrink-0" />
                  {open && <span className="truncate">{entry.label}</span>}
                </button>
              );

              if (!open) {
                return (
                  <Tooltip key={entry.path}>
                    <TooltipTrigger asChild>{btn}</TooltipTrigger>
                    <TooltipContent side="right">{entry.label}</TooltipContent>
                  </Tooltip>
                );
              }
              return btn;
            })}
          </ul>
        )}
      </nav>

      <UserSection open={open} />
    </aside>
  );
}
